<template>
  <AppModal
    :show="true"
    :title="$t('documents.confirmDelete')"
    icon="exclamation-triangle"
    size="medium"
    :gradientHeader="true"
    :loading="loading"
    @close="$emit('cancel')"
  >
    <!-- Main content -->
    <div class="delete-confirmation__content">
      <div class="delete-confirmation__warning">
        <svg class="delete-confirmation__warning-icon" viewBox="0 0 24 24" width="20" height="20">
          <path fill="currentColor" d="M13 14H11V9H13M13 18H11V16H13M1 21H23L12 2L1 21Z"/>
        </svg>
        <p class="delete-confirmation__warning-text">{{ $t('documents.deleteWarning', { filename }) }}</p>
      </div>
      
      <div class="delete-confirmation__details">
        <div class="delete-confirmation__detail-item">
          <span class="delete-confirmation__detail-label">{{ $t('documents.filename') }}:</span>
          <span class="delete-confirmation__detail-value">{{ filename }}</span>
        </div>
        <div class="delete-confirmation__detail-item" v-if="docType">
          <span class="delete-confirmation__detail-label">{{ $t('documents.documentType') }}:</span>
          <span class="delete-confirmation__detail-value">{{ $t(`${docType}`) }}</span>
        </div>
        <div class="delete-confirmation__detail-item" v-if="description">
          <span class="delete-confirmation__detail-label">{{ $t('documents.description') }}:</span>
          <span class="delete-confirmation__detail-value">{{ description }}</span>
        </div>
      </div>
    </div>    <!-- Custom footer -->
    <template #footer>
      <div class="delete-confirmation__actions">
        <AppButton
          type="button"
          variant="outline"
          size="medium"
          @click="$emit('cancel')"
          class="delete-confirmation__btn delete-confirmation__btn--cancel"
        >
          {{ $t('common.cancel') }}
        </AppButton>
        <AppButton
          type="button"
          variant="danger"
          size="medium"
          @click.stop="$emit('confirm')"
          :disabled="loading"
          :loading="loading"
          class="delete-confirmation__btn delete-confirmation__btn--confirm"
        >
          {{ $t('documents.delete') }}
        </AppButton>
      </div>
    </template>
  </AppModal>
</template>

<script setup lang="ts">
import AppModal from '../../base/Modal.vue';
import AppButton from '../../base/AppButton.vue';

interface Props {
  filename: string;
  docType: string;
  description: string;
  loading: boolean;
}

interface Emits {
  (e: 'confirm'): void;
  (e: 'cancel'): void;
}

const props = withDefaults(defineProps<Props>(), {
  docType: '',
  description: '',
  loading: false
});

const emit = defineEmits<Emits>();
</script>

<style scoped>
/* ==========================================================================
   DELETE CONFIRMATION MODAL COMPONENT
   ========================================================================== */

/* Content */
.delete-confirmation__content {
  display: flex;
  flex-direction: column;
  gap: var(--space-lg, 1.5rem);
}

/* Warning Section */
.delete-confirmation__warning {
  display: flex;
  align-items: flex-start;
  gap: var(--space-md, 1rem);
  padding: var(--space-md, 1rem);
  background: var(--danger-50, #fef2f2);
  border: 1px solid var(--danger-200, #fecaca);
  border-radius: var(--radius-md, 8px);
  border-left: 4px solid var(--danger-500, #ef4444);
}

.delete-confirmation__warning-icon {
  color: var(--danger-500, #ef4444);
  flex-shrink: 0;
  margin-top: 2px;
}

.delete-confirmation__warning-text {
  color: var(--danger-700, #b91c1c);
  font-weight: var(--font-weight-semibold, 600);
  margin: 0;
  line-height: 1.5;
}

/* Details Section */
.delete-confirmation__details {
  display: flex;
  flex-direction: column;
  gap: var(--space-md, 1rem);
}

.delete-confirmation__detail-item {
  display: flex;
  flex-direction: column;
  gap: var(--space-xs, 0.25rem);
  padding: var(--space-md, 1rem);
  background: var(--surface-secondary, #f8f9fa);
  border-radius: var(--radius-base, 6px);
  border: 1px solid var(--border-light, #e9ecef);
}

.delete-confirmation__detail-label {
  font-size: var(--font-size-sm, 0.875rem);
  font-weight: var(--font-weight-semibold, 600);
  color: var(--text-secondary, #6c757d);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.delete-confirmation__detail-value {
  font-size: var(--font-size-base, 1rem);
  color: var(--text-primary, #212529);
  font-weight: var(--font-weight-medium, 500);
  word-break: break-word;
}

/* Footer Actions */
.delete-confirmation__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-md, 1rem);
}

.delete-confirmation__btn {
  min-width: 100px;
}

.delete-confirmation__btn--confirm {
  background: var(--danger-500, #ef4444);
  border-color: var(--danger-500, #ef4444);
  color: white;
}

.delete-confirmation__btn--confirm:hover:not(:disabled) {
  background: var(--danger-600, #dc2626);
  border-color: var(--danger-600, #dc2626);
  transform: translateY(-1px);
  box-shadow: var(--shadow-lg, 0 10px 15px -3px rgba(0, 0, 0, 0.1));
}

.delete-confirmation__btn--confirm:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Responsive Design */
@media (max-width: 768px) {
  .delete-confirmation__actions {
    flex-direction: column-reverse;
    gap: var(--space-sm, 0.75rem);
  }

  .delete-confirmation__btn {
    width: 100%;
  }
}

/* Dark Mode Support */
@media (prefers-color-scheme: dark) {
  .delete-confirmation__warning {
    background: rgba(239, 68, 68, 0.1);
    border-color: rgba(239, 68, 68, 0.3);
  }

  .delete-confirmation__warning-text {
    color: var(--danger-400, #f87171);
  }
}

/* High Contrast Mode */
@media (prefers-contrast: high) {
  .delete-confirmation__warning {
    border: 2px solid var(--danger-500, #ef4444);
  }

  .delete-confirmation__detail-item {
    border: 1px solid var(--text-primary, #212529);
  }
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  .delete-confirmation__btn--confirm:hover:not(:disabled) {
    transform: none;
  }
}
</style>
